// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const INDUSTRIES = ['All', 'Agriculture', 'Healthcare', 'Technology', 'Energy', 'Construction', 'Automotive', 'Mining', 'Cannabis', 'Real Estate', 'Wellness', 'Insurance', 'Events', 'Beauty', 'Logistics'];
const STAGES = ['All', 'Concept', 'Validated', 'Prototype', 'Patent-Ready'];
const INVESTMENT_RANGES = ['All', '$10K-$50K', '$50K-$200K', '$200K+'];
const SORT_OPTIONS = ['Newest', 'Most Interest', 'Highest Potential'];

const STAGE_COLORS = {
  'Concept': { bg: 'rgba(99,102,241,0.15)', text: '#818cf8' },
  'Validated': { bg: 'rgba(16,185,129,0.15)', text: '#10b981' },
  'Prototype': { bg: 'rgba(245,158,11,0.15)', text: '#f59e0b' },
  'Patent-Ready': { bg: 'rgba(139,92,246,0.15)', text: '#8b5cf6' },
};

const DEMO_IDEAS = [
  {
    id: 1,
    title: 'Autonomous Grain Sorting System',
    industry: 'Agriculture',
    stage: 'Patent-Ready',
    blurredDesc: 'An AI-driven conveyor system that uses multi-spectral imaging to sort grain by quality grade in real time, reducing waste by up to 40%.',
    fullDesc: 'This patent-ready system combines multi-spectral camera arrays with edge-computing ML models to classify grain kernels at 2,000 units/second. The proprietary sorting mechanism uses micro-pneumatic actuators to redirect kernels into grade-specific bins. Field trials on three Saskatchewan farms showed a 42% reduction in grade-mixing waste and a 28% increase in premium-grade yield capture.',
    uniqueness: 87,
    investmentRange: '$50K-$200K',
    investmentEstimate: '$85,000 - $140,000',
    date: '2026-02-08',
    views: 312,
    interests: 24,
  },
  {
    id: 2,
    title: 'IoT Livestock Health Monitor',
    industry: 'Agriculture',
    stage: 'Validated',
    blurredDesc: 'Wearable biosensor tags for cattle that transmit real-time vitals and behavioral anomalies to a cloud dashboard, enabling early disease detection.',
    fullDesc: 'Ear-tag mounted biosensors measure body temperature, rumination cycles, heart rate variability, and GPS movement patterns. A lightweight TinyML model runs on-device to flag anomalies before transmitting compressed alerts via LoRaWAN. Validated across 1,200 head of cattle with 94% early detection accuracy for bovine respiratory disease, averaging 3.2 days advance warning before clinical symptoms.',
    uniqueness: 74,
    investmentRange: '$50K-$200K',
    investmentEstimate: '$60,000 - $120,000',
    date: '2026-02-06',
    views: 189,
    interests: 18,
  },
  {
    id: 3,
    title: 'AI-Powered Wound Assessment Tool',
    industry: 'Healthcare',
    stage: 'Prototype',
    blurredDesc: 'A smartphone-based imaging tool that uses computer vision to measure wound dimensions, classify tissue types, and generate standardized care reports.',
    fullDesc: 'The prototype uses a calibrated smartphone camera with a disposable color-reference sticker to capture wound images. A CNN model segments the wound bed, identifies granulation, slough, and necrotic tissue percentages, and calculates area and depth estimates. Generates PDF reports compliant with CMS wound documentation standards. Tested in two long-term care facilities with 91% agreement with expert clinician assessments.',
    uniqueness: 81,
    investmentRange: '$50K-$200K',
    investmentEstimate: '$70,000 - $150,000',
    date: '2026-02-05',
    views: 267,
    interests: 31,
  },
  {
    id: 4,
    title: 'Micro-Grid Battery Swap Station',
    industry: 'Energy',
    stage: 'Concept',
    blurredDesc: 'A modular battery swap station for rural communities that integrates with solar micro-grids to provide rapid EV charging without grid upgrades.',
    fullDesc: 'The concept envisions a shipping-container-sized swap station housing 24 standardized battery modules charged by co-located solar arrays with battery buffer storage. Vehicles pull in, robotic arms exchange depleted modules for charged ones in under 4 minutes. Revenue model includes per-swap fees and grid arbitrage during peak demand. Preliminary feasibility study shows break-even at 18 months in underserved rural corridors.',
    uniqueness: 68,
    investmentRange: '$200K+',
    investmentEstimate: '$350,000 - $500,000',
    date: '2026-02-04',
    views: 445,
    interests: 37,
  },
  {
    id: 5,
    title: 'Smart Concrete Curing Sensor',
    industry: 'Construction',
    stage: 'Validated',
    blurredDesc: 'Embeddable wireless sensors that monitor concrete temperature, humidity, and maturity in real time, replacing destructive cylinder break tests.',
    fullDesc: 'Disposable RFID-powered sensors are embedded during pour and transmit temperature and impedance data to a handheld reader. A maturity algorithm calculates equivalent age and compressive strength estimates conforming to ASTM C1074. Validated on 14 commercial pours with strength predictions within 5% of lab-tested cylinders at 7 and 28 days. Eliminates the need for casting, transporting, and breaking test cylinders.',
    uniqueness: 72,
    investmentRange: '$10K-$50K',
    investmentEstimate: '$25,000 - $45,000',
    date: '2026-02-03',
    views: 198,
    interests: 15,
  },
  {
    id: 6,
    title: 'Predictive Fleet Tire Manager',
    industry: 'Logistics',
    stage: 'Prototype',
    blurredDesc: 'Machine learning system that predicts tire failures across commercial fleets using TPMS data, road surface analytics, and historical wear patterns.',
    fullDesc: 'Ingests real-time TPMS pressure and temperature readings, GPS route data, load manifests, and historical tire service records. A gradient-boosted model predicts remaining tread life and blowout probability per axle position. Dashboard prioritizes replacements by risk score and schedules service at the nearest partner shop. Prototype tested on a 200-truck fleet, catching 89% of failure events 7+ days in advance, reducing roadside breakdowns by 61%.',
    uniqueness: 79,
    investmentRange: '$50K-$200K',
    investmentEstimate: '$90,000 - $160,000',
    date: '2026-02-02',
    views: 221,
    interests: 22,
  },
  {
    id: 7,
    title: 'Cannabis Terpene Profiling Scanner',
    industry: 'Cannabis',
    stage: 'Patent-Ready',
    blurredDesc: 'A handheld near-infrared scanner that quantifies terpene profiles in cannabis flower within seconds, replacing costly lab chromatography.',
    fullDesc: 'The device uses a miniaturized NIR spectrometer with a proprietary chemometric model trained on 8,000+ lab-verified samples. It identifies and quantifies 23 primary terpenes plus total THC and CBD estimates in under 10 seconds. Results display on a companion app with COA-format export. Patent claims cover the calibration transfer method enabling consistent readings across manufacturing batches of the device. Accuracy within 8% of HPLC lab results.',
    uniqueness: 91,
    investmentRange: '$50K-$200K',
    investmentEstimate: '$110,000 - $180,000',
    date: '2026-01-30',
    views: 389,
    interests: 42,
  },
  {
    id: 8,
    title: 'Automated Mine Ventilation Optimizer',
    industry: 'Mining',
    stage: 'Concept',
    blurredDesc: 'An AI control system that dynamically adjusts underground mine ventilation based on real-time gas readings, personnel location, and energy pricing.',
    fullDesc: 'The concept integrates gas sensor arrays, personnel tracking beacons, and variable-frequency drive controllers into a reinforcement-learning optimization loop. The system routes airflow to active headings, reduces flow in evacuated zones, and pre-ventilates before shift changes. Energy simulations based on two potash mines suggest 30-45% reduction in ventilation electricity costs while maintaining regulatory compliance with all provincial occupational health standards.',
    uniqueness: 76,
    investmentRange: '$200K+',
    investmentEstimate: '$280,000 - $420,000',
    date: '2026-01-28',
    views: 156,
    interests: 11,
  },
  {
    id: 9,
    title: 'Real Estate Comp Analyzer with AR',
    industry: 'Real Estate',
    stage: 'Concept',
    blurredDesc: 'An augmented reality app that overlays comparable sale data, tax assessments, and neighborhood analytics when pointing a phone at any property.',
    fullDesc: 'The app uses ARKit/ARCore to identify properties via GPS, street-view matching, and parcel boundary data. Overlays include last sale price, tax assessment, price-per-sqft comps within a configurable radius, days-on-market trends, and school/crime score badges. Data sourced from MLS IDX feeds, county assessor APIs, and census data. Concept validated with 40 real estate agents who rated it 4.6/5 for field utility.',
    uniqueness: 63,
    investmentRange: '$50K-$200K',
    investmentEstimate: '$75,000 - $130,000',
    date: '2026-01-26',
    views: 278,
    interests: 29,
  },
  {
    id: 10,
    title: 'Wearable UV Dosimeter for Outdoor Workers',
    industry: 'Wellness',
    stage: 'Validated',
    blurredDesc: 'A clip-on UV exposure tracker that logs cumulative dose, alerts workers at threshold levels, and generates compliance reports for OSHA standards.',
    fullDesc: 'The device uses a broadband UV photodiode with a cosine-corrected diffuser to measure erythemal-weighted UV dose in real time. BLE syncs data to a supervisor dashboard showing per-worker cumulative exposure against ACGIH TLV limits. Audible and haptic alerts fire at 50%, 80%, and 100% of daily thresholds. Validated across three construction sites over a full summer season with 97% uptime and dose readings within 12% of research-grade instruments.',
    uniqueness: 70,
    investmentRange: '$10K-$50K',
    investmentEstimate: '$30,000 - $48,000',
    date: '2026-01-24',
    views: 134,
    interests: 9,
  },
  {
    id: 11,
    title: 'AI Insurance Claims Photo Verifier',
    industry: 'Insurance',
    stage: 'Prototype',
    blurredDesc: 'A deep learning model that analyzes insurance claim photos for inconsistencies, metadata anomalies, and signs of staging or prior damage.',
    fullDesc: 'The system ingests claim photos and runs them through a multi-stage pipeline: EXIF metadata validation (timestamp, GPS, device consistency), image forensics (ELA, copy-move detection, splicing analysis), and a damage-classification CNN that flags severity inconsistencies with the written claim narrative. Prototype evaluated on 5,000 historical claims with a 78% detection rate for confirmed fraudulent submissions and a 4.2% false positive rate, representing a potential $2.3M annual savings for a mid-size carrier.',
    uniqueness: 83,
    investmentRange: '$50K-$200K',
    investmentEstimate: '$95,000 - $175,000',
    date: '2026-01-22',
    views: 302,
    interests: 35,
  },
  {
    id: 12,
    title: 'Modular Pop-Up Event Stage System',
    industry: 'Events',
    stage: 'Prototype',
    blurredDesc: 'A flatpack modular stage system that two people can assemble in under 30 minutes, with integrated LED lighting and weatherproof audio mounts.',
    fullDesc: 'Each module is a 4x4 ft aluminum-frame panel with a composite deck rated for 150 psf. Panels interlock with cam-lock fasteners requiring no tools. Integrated channels house LED strip lighting controlled via a Bluetooth app. Audio mount points accept standard speaker poles and are rated for 80 lb lateral loads. Prototype tested at 12 outdoor events with setup times averaging 22 minutes for a 16x12 ft stage. Rental model projects 340% annual ROI at 3 events per weekend.',
    uniqueness: 65,
    investmentRange: '$50K-$200K',
    investmentEstimate: '$80,000 - $140,000',
    date: '2026-01-20',
    views: 176,
    interests: 14,
  },
  {
    id: 13,
    title: 'Smart Salon Color Mixing Assistant',
    industry: 'Beauty',
    stage: 'Concept',
    blurredDesc: 'An app-connected dispenser that mixes custom hair color formulas based on AI analysis of client hair photos and desired outcome references.',
    fullDesc: 'The system uses a multi-cartridge dispenser loaded with base pigments and developers. A stylist photographs the client\'s current hair color and selects a target swatch or reference image. A color-science model calculates the formula accounting for starting level, porosity estimate, and desired tone. The dispenser mixes the exact volume needed, reducing product waste by an estimated 35%. Concept tested with 15 stylists using manual mixing of the AI-recommended formulas, with 88% client satisfaction on first application.',
    uniqueness: 71,
    investmentRange: '$50K-$200K',
    investmentEstimate: '$65,000 - $110,000',
    date: '2026-01-18',
    views: 203,
    interests: 19,
  },
  {
    id: 14,
    title: 'Autonomous Pothole Repair Drone',
    industry: 'Construction',
    stage: 'Concept',
    blurredDesc: 'A ground-based autonomous robot that detects, maps, and fills potholes with rapid-cure polymer compound during off-peak traffic hours.',
    fullDesc: 'The robot uses LIDAR and stereo cameras to scan road surfaces, identify potholes by depth and area, and autonomously navigate to each repair site. A heated polymer reservoir dispenses rapid-cure filler material that bonds at -20C and reaches full strength in 45 minutes. GPS logging creates a municipal repair record with before/after depth measurements. Concept modeling suggests a single unit could repair 40-60 potholes per 8-hour overnight shift, at roughly 30% of the cost of traditional crew-based patching.',
    uniqueness: 77,
    investmentRange: '$200K+',
    investmentEstimate: '$400,000 - $650,000',
    date: '2026-01-15',
    views: 521,
    interests: 48,
  },
  {
    id: 15,
    title: 'Directional Drilling Decision Engine',
    industry: 'Energy',
    stage: 'Patent-Ready',
    blurredDesc: 'A closed-loop decision system for directional drilling that integrates survey data, motor yield curves, and formation models to optimize wellbore placement.',
    fullDesc: 'The system implements a continuous optimization loop: ingest MWD survey data, compute minimum curvature trajectory, compare against the planned well path, calculate required motor yield and slide distances, render a real-time steering display for the driller, and feed the next survey back into the loop. Patent claims cover the integrated decision workflow and the predictive slide-length algorithm that accounts for formation anisotropy. Field tested on 14 WCSB horizontal wells with an average 18% reduction in correction runs and 12% faster curve-section drilling times.',
    uniqueness: 94,
    investmentRange: '$200K+',
    investmentEstimate: '$250,000 - $380,000',
    date: '2026-01-12',
    views: 467,
    interests: 53,
  },
];

const STATS = [
  { label: 'Ideas Listed', value: 47, color: '#6366f1' },
  { label: 'Under Review', value: 12, color: '#f59e0b' },
  { label: 'In Development', value: 8, color: '#10b981' },
  { label: 'Patents Filed', value: 21, color: '#8b5cf6' },
];

function CircularProgress({ value, size = 48, stroke = 4 }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  const color = value >= 80 ? '#10b981' : value >= 60 ? '#f59e0b' : '#ef4444';

  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--border)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
      />
      <text
        x={size / 2}
        y={size / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fill="var(--text)"
        fontSize={size * 0.26}
        fontWeight={700}
        style={{ transform: 'rotate(90deg)', transformOrigin: 'center' }}
      >
        {value}
      </text>
    </svg>
  );
}

export default function IdeaMarketplacePage() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [industryFilter, setIndustryFilter] = useState('All');
  const [stageFilter, setStageFilter] = useState('All');
  const [investmentFilter, setInvestmentFilter] = useState('All');
  const [sortBy, setSortBy] = useState('Newest');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [ndaAgreed, setNdaAgreed] = useState(false);
  const [revealedIds, setRevealedIds] = useState(new Set());

  const handleExpressInterest = (idea) => {
    if (revealedIds.has(idea.id)) return;
    setSelectedIdea(idea);
    setNdaAgreed(false);
    setModalOpen(true);
  };

  const handleSignNDA = () => {
    if (!ndaAgreed || !selectedIdea) return;
    setRevealedIds((prev) => new Set([...prev, selectedIdea.id]));
    setModalOpen(false);
    setSelectedIdea(null);
  };

  const filteredIdeas = DEMO_IDEAS
    .filter((idea) => {
      if (search && !idea.title.toLowerCase().includes(search.toLowerCase())) return false;
      if (industryFilter !== 'All' && idea.industry !== industryFilter) return false;
      if (stageFilter !== 'All' && idea.stage !== stageFilter) return false;
      if (investmentFilter !== 'All' && idea.investmentRange !== investmentFilter) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'Newest') return new Date(b.date) - new Date(a.date);
      if (sortBy === 'Most Interest') return b.interests - a.interests;
      if (sortBy === 'Highest Potential') return b.uniqueness - a.uniqueness;
      return 0;
    });

  return (
    <div className="page-body" style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ color: 'var(--text)', fontSize: 28, fontWeight: 700, margin: '0 0 6px' }}>
          Idea Marketplace
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 15, margin: 0, lineHeight: 1.5 }}>
          Browse innovation opportunities. Sign an NDA to unlock full details.
        </p>
      </div>

      {/* Filter Bar */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        marginBottom: 20,
        padding: 16,
        background: 'var(--card)',
        borderRadius: 10,
        border: '1px solid var(--border)',
      }}>
        <input
          type="text"
          placeholder="Search ideas..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: '1 1 200px',
            padding: '9px 14px',
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'var(--bg)',
            color: 'var(--text)',
            fontSize: 14,
            outline: 'none',
          }}
        />
        <select
          value={industryFilter}
          onChange={(e) => setIndustryFilter(e.target.value)}
          style={{
            padding: '9px 14px',
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'var(--bg)',
            color: 'var(--text)',
            fontSize: 14,
            cursor: 'pointer',
            minWidth: 140,
          }}
        >
          {INDUSTRIES.map((ind) => (
            <option key={ind} value={ind}>{ind === 'All' ? 'All Industries' : ind}</option>
          ))}
        </select>
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          style={{
            padding: '9px 14px',
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'var(--bg)',
            color: 'var(--text)',
            fontSize: 14,
            cursor: 'pointer',
            minWidth: 130,
          }}
        >
          {STAGES.map((s) => (
            <option key={s} value={s}>{s === 'All' ? 'All Stages' : s}</option>
          ))}
        </select>
        <select
          value={investmentFilter}
          onChange={(e) => setInvestmentFilter(e.target.value)}
          style={{
            padding: '9px 14px',
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'var(--bg)',
            color: 'var(--text)',
            fontSize: 14,
            cursor: 'pointer',
            minWidth: 140,
          }}
        >
          {INVESTMENT_RANGES.map((r) => (
            <option key={r} value={r}>{r === 'All' ? 'All Investments' : r}</option>
          ))}
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{
            padding: '9px 14px',
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'var(--bg)',
            color: 'var(--text)',
            fontSize: 14,
            cursor: 'pointer',
            minWidth: 150,
          }}
        >
          {SORT_OPTIONS.map((s) => (
            <option key={s} value={s}>Sort: {s}</option>
          ))}
        </select>
      </div>

      {/* Stats Bar */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 14,
        marginBottom: 24,
      }}>
        {STATS.map((stat) => (
          <div key={stat.label} style={{
            background: 'var(--card)',
            borderRadius: 10,
            border: '1px solid var(--border)',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: `${stat.color}18`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              fontWeight: 800,
              color: stat.color,
            }}>
              {stat.value}
            </div>
            <span style={{ color: 'var(--text-muted)', fontSize: 14, fontWeight: 500 }}>
              {stat.label}
            </span>
          </div>
        ))}
      </div>

      {/* Results count */}
      <div style={{ marginBottom: 16, color: 'var(--text-muted)', fontSize: 13 }}>
        Showing {filteredIdeas.length} of {DEMO_IDEAS.length} ideas
      </div>

      {/* Idea Cards Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 18,
      }}>
        {filteredIdeas.map((idea) => {
          const revealed = revealedIds.has(idea.id);
          const stageColor = STAGE_COLORS[idea.stage] || { bg: 'rgba(99,102,241,0.15)', text: '#818cf8' };

          return (
            <div key={idea.id} style={{
              background: 'var(--card)',
              borderRadius: 12,
              border: '1px solid var(--border)',
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              transition: 'border-color 0.2s',
            }}>
              {/* Title */}
              <h3 style={{
                color: 'var(--text)',
                fontSize: 16,
                fontWeight: 600,
                margin: 0,
                lineHeight: 1.4,
              }}>
                {idea.title}
              </h3>

              {/* Badges row */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span style={{
                  padding: '3px 10px',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  background: 'rgba(99,102,241,0.12)',
                  color: '#818cf8',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}>
                  {idea.industry}
                </span>
                <span style={{
                  padding: '3px 10px',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  background: stageColor.bg,
                  color: stageColor.text,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}>
                  {idea.stage}
                </span>
              </div>

              {/* Description (blurred or revealed) */}
              <div style={{ position: 'relative', minHeight: 48 }}>
                {revealed ? (
                  <div>
                    <p style={{
                      color: 'var(--text)',
                      fontSize: 13,
                      lineHeight: 1.6,
                      margin: '0 0 10px',
                    }}>
                      {idea.fullDesc}
                    </p>
                    <div style={{
                      padding: '10px 12px',
                      borderRadius: 8,
                      background: 'rgba(16,185,129,0.08)',
                      border: '1px solid rgba(16,185,129,0.2)',
                      marginBottom: 4,
                    }}>
                      <div style={{ fontSize: 11, color: '#10b981', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        NDA Signed — Full Access
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        Contact inventor through the platform messaging system or request a direct introduction via the Innovation Hub team.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <p style={{
                      color: 'var(--text-muted)',
                      fontSize: 13,
                      lineHeight: 1.6,
                      margin: 0,
                      filter: 'blur(5px)',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}>
                      {idea.blurredDesc}
                    </p>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: 6,
                        background: 'rgba(99,102,241,0.15)',
                        color: '#818cf8',
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: 0.3,
                      }}>
                        Sign NDA to reveal
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Uniqueness + Investment row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CircularProgress value={idea.uniqueness} size={48} stroke={4} />
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>Uniqueness</div>
                    <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>Score</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>Investment Est.</div>
                  <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>{idea.investmentEstimate}</div>
                </div>
              </div>

              {/* Meta row: date + views */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: '1px solid var(--border)',
                paddingTop: 10,
                marginTop: 2,
              }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {new Date(idea.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {idea.views.toLocaleString()} views
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {idea.interests} interested
                  </span>
                </div>
              </div>

              {/* Express Interest Button */}
              <button
                onClick={() => handleExpressInterest(idea)}
                disabled={revealed}
                style={{
                  width: '100%',
                  padding: '10px 0',
                  borderRadius: 8,
                  border: 'none',
                  background: revealed ? 'rgba(16,185,129,0.15)' : 'var(--primary)',
                  color: revealed ? '#10b981' : '#fff',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: revealed ? 'default' : 'pointer',
                  transition: 'opacity 0.2s',
                  opacity: revealed ? 1 : undefined,
                }}
                onMouseEnter={(e) => { if (!revealed) e.currentTarget.style.opacity = '0.85'; }}
                onMouseLeave={(e) => { if (!revealed) e.currentTarget.style.opacity = '1'; }}
              >
                {revealed ? 'Details Unlocked' : 'Express Interest'}
              </button>
            </div>
          );
        })}
      </div>

      {filteredIdeas.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: 60,
          color: 'var(--text-muted)',
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>&#128270;</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>No ideas match your filters</div>
          <div style={{ fontSize: 14 }}>Try adjusting your search terms or filter criteria.</div>
        </div>
      )}

      {/* Express Interest Modal */}
      {modalOpen && selectedIdea && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => setModalOpen(false)}
        >
          <div
            style={{
              background: 'var(--card)',
              borderRadius: 14,
              border: '1px solid var(--border)',
              padding: 32,
              maxWidth: 480,
              width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 20,
            }}>
              <div style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                background: 'rgba(99,102,241,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
              }}>
                &#128274;
              </div>
              <div>
                <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700, margin: 0 }}>
                  NDA Required
                </h2>
                <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>
                  Confidentiality agreement for idea access
                </p>
              </div>
            </div>

            {/* Idea being accessed */}
            <div style={{
              background: 'var(--bg)',
              borderRadius: 10,
              border: '1px solid var(--border)',
              padding: 14,
              marginBottom: 20,
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                {selectedIdea.title}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 600,
                  background: 'rgba(99,102,241,0.12)',
                  color: '#818cf8',
                  textTransform: 'uppercase',
                }}>
                  {selectedIdea.industry}
                </span>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 600,
                  background: STAGE_COLORS[selectedIdea.stage]?.bg || 'rgba(99,102,241,0.15)',
                  color: STAGE_COLORS[selectedIdea.stage]?.text || '#818cf8',
                  textTransform: 'uppercase',
                }}>
                  {selectedIdea.stage}
                </span>
              </div>
            </div>

            {/* NDA Terms */}
            <div style={{
              background: 'var(--bg)',
              borderRadius: 10,
              border: '1px solid var(--border)',
              padding: 16,
              marginBottom: 20,
              maxHeight: 160,
              overflowY: 'auto',
            }}>
              <p style={{ color: 'var(--text)', fontSize: 13, lineHeight: 1.7, margin: '0 0 10px' }}>
                To view the full details of this idea, you must agree to a mutual Non-Disclosure Agreement (NDA) that protects both the inventor and the viewer.
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.6, margin: '0 0 8px' }}>
                By signing, you agree to the following terms:
              </p>
              <ul style={{ color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.8, margin: 0, paddingLeft: 18 }}>
                <li>You will not disclose, share, or reproduce any proprietary details of this idea without written consent from the inventor.</li>
                <li>All technical descriptions, business models, and data remain the intellectual property of the original inventor.</li>
                <li>This NDA is mutual — your identity and interest are also protected.</li>
                <li>This agreement is governed by the laws of the Province of Alberta, Canada.</li>
                <li>Violation may result in legal action and removal from the Innovation Hub platform.</li>
              </ul>
            </div>

            {/* Checkbox */}
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              marginBottom: 24,
              cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={ndaAgreed}
                onChange={(e) => setNdaAgreed(e.target.checked)}
                style={{
                  marginTop: 2,
                  width: 18,
                  height: 18,
                  accentColor: 'var(--primary)',
                  cursor: 'pointer',
                }}
              />
              <span style={{ color: 'var(--text)', fontSize: 13, lineHeight: 1.5 }}>
                I have read and agree to the mutual NDA terms. I understand that any breach of this agreement may result in legal consequences.
              </span>
            </label>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setModalOpen(false)}
                style={{
                  flex: 1,
                  padding: '11px 0',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'transparent',
                  color: 'var(--text-muted)',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSignNDA}
                disabled={!ndaAgreed}
                style={{
                  flex: 2,
                  padding: '11px 0',
                  borderRadius: 8,
                  border: 'none',
                  background: ndaAgreed ? 'var(--primary)' : 'rgba(99,102,241,0.3)',
                  color: ndaAgreed ? '#fff' : 'rgba(255,255,255,0.4)',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: ndaAgreed ? 'pointer' : 'not-allowed',
                  transition: 'background 0.2s, color 0.2s',
                }}
              >
                Sign NDA &amp; View Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
